import React, { useEffect, useState } from 'react';
import { usersAPI, clientsAPI, sitesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTimeBritish } from '../utils/dateUtils';
import './UserList.css';
import additionIcon from '../assets/icons/addition.png';

function formatRole(role: string): string {
  switch (role) {
    case 'SuperAdmin':   return 'Super Admin';
    case 'SuperUser':    return 'Super User';
    case 'FleetManager': return 'Fleet Manager';
    case 'FleetManagerAdmin': return 'Fleet Manager - Admin';
    case 'ClientAdmin':   return 'Client Admin';
    case 'MTA':          return 'MTA';
    case 'ClaimsManager': return 'Claims Manager';
    case 'Onboarder':     return 'Onboarder';
    case 'Admin':        return 'Compliance';
    default:             return role;
  }
}

function roleBadgeClass(role: string): string {
  switch (role) {
    case 'SuperAdmin':   return 'superadmin';
    case 'SuperUser':    return 'superuser';
    case 'FleetManager': return 'fleetmanager';
    case 'FleetManagerAdmin': return 'fleetmanager';
    case 'ClientAdmin':   return 'fleetmanager';
    case 'MTA':          return 'mta';
    case 'ClaimsManager': return 'claimsmanager';
    case 'Onboarder':     return 'mta';
    case 'Admin':        return 'compliance';
    default:             return role.toLowerCase();
  }
}

const STAFF_ROLES = new Set(['SuperAdmin', 'SuperUser', 'Admin', 'MTA', 'Onboarder', 'ClaimsManager', 'FleetManager', 'FleetManagerAdmin']);

interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  clientId?: number;
  clientName?: string;
  siteId?: number;
  siteName?: string;
  siteIds?: number[];
  siteNames?: string[];
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
  lockedUntil?: string | null;
}

interface Client {
  id: number;
  companyName: string;
}

interface Site {
  id: number;
  name: string;
}

const EMPTY_FORM = {
  username: '', email: '', password: '',
  firstName: '', lastName: '',
  role: 'Client',
  clientId: undefined as number | undefined,
  siteId: undefined as number | undefined,
};

const UserList: React.FC = () => {
  const { isSuperUser, isFleetManager, user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'SuperAdmin';
  const isFleetManagerAdmin = currentUser?.role === 'FleetManagerAdmin';
  const isOnboarder = currentUser?.role === 'Onboarder';

  const [users, setUsers]     = useState<User[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [sitesForClient, setSitesForClient]     = useState<Site[]>([]);
  const [showForm, setShowForm]       = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [sendingInvite, setSendingInvite] = useState<number | null>(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [formSiteIds, setFormSiteIds] = useState<number[]>([]);

  // Change-site-for-user modal state
  const [showChangeSiteUserModal, setShowChangeSiteUserModal] = useState(false);
  const [changeSiteUserTarget, setChangeSiteUserTarget] = useState<User | null>(null);
  const [changeSiteUserNewSiteId, setChangeSiteUserNewSiteId] = useState<number | undefined>(undefined);
  const [changeSiteUserSites, setChangeSiteUserSites] = useState<Site[]>([]);
  const [changeSiteUserSubmitting, setChangeSiteUserSubmitting] = useState(false);

  const clientRequired = !STAFF_ROLES.has(formData.role);

  useEffect(() => { loadUsers(); loadClients(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!formData.clientId) { setSitesForClient([]); return; }
    sitesAPI.getByClient(formData.clientId)
      .then(r => setSitesForClient(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSitesForClient([]));
  }, [formData.clientId]);

  const loadUsers = async () => {
    try {
      const r = await usersAPI.getAll();
      setUsers(r.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const loadClients = async () => {
    try {
      const r = await clientsAPI.getAll();
      setClients(r.data.sort((a: Client, b: Client) => a.companyName.localeCompare(b.companyName)));
      // Auto-expand the fleet manager's own client accordion immediately
      if (isFleetManager && currentUser?.clientId) {
        setSelectedClientId(currentUser.clientId);
      }
    } catch { /* ignore */ }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const username = formData.username.trim();
    if (!editingUser || formData.username) {
      if (username.includes('@') || /\s/.test(username)) {
        alert('Username cannot be an email address or contain spaces. Use something like FirstLast (e.g. EdytaHaines).');
        return;
      }
      if (username.length < 3) {
        alert('Username must be at least 3 characters.');
        return;
      }
    }
    try {
        if (editingUser) {
        if (isFleetManager) {
          await usersAPI.fleetManagerUpdate(editingUser.id, {
            email: formData.email, firstName: formData.firstName, lastName: formData.lastName,
            primarySiteId: formData.siteId, siteIds: formSiteIds,
          });
        } else {
          const { password, ...updateData } = formData;
          await usersAPI.update(editingUser.id, { ...updateData, username, siteIds: formSiteIds });
        }
      } else if (isFleetManager) {
        await usersAPI.fleetManagerCreate({ ...formData, username, siteIds: formSiteIds, primarySiteId: formData.siteId });
      } else {
        await usersAPI.create({ ...formData, username });
      }
      loadUsers(); resetForm();
    } catch (err: any) { alert(err?.response?.data?.message || 'Failed to save user'); }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({ username: user.username, email: user.email, password: '',
      firstName: user.firstName, lastName: user.lastName,
      role: user.role, clientId: user.clientId, siteId: user.siteId });
    setFormSiteIds(user.siteIds ?? []);
    setShowForm(true);
  };

  const handleToggleActive = async (id: number, current: boolean) => {
    try {
      if (isFleetManager) {
        if (!current) return; // fleet managers can only deactivate, not reactivate
        await usersAPI.fleetManagerDeactivate(id);
      } else {
        current ? await usersAPI.deactivate(id) : await usersAPI.activate(id);
      }
      loadUsers();
    } catch { alert('Failed to update user status'); }
  };

  const handleResetPassword = async (id: number) => {
    const pw = prompt('Enter new password:');
    if (!pw) return;
    try { await usersAPI.resetPassword(id, pw); alert('Password reset successfully'); }
    catch { alert('Failed to reset password'); }
  };

  const handleSendInvite = async (user: User) => {
    if (!window.confirm(`Send a portal invitation to ${user.email}?`)) return;
    setSendingInvite(user.id);
    try { await usersAPI.sendInvite(user.id); alert(`Invitation sent to ${user.email}.`); }
    catch (err: any) { alert(err?.response?.data?.message || 'Failed to send invitation.'); }
    finally { setSendingInvite(null); }
  };

  const handleUnlock = async (user: User) => {
    if (!window.confirm(`Unlock account for ${user.firstName} ${user.lastName} (${user.username})?`)) return;
    try {
      await usersAPI.unlockUser(user.id);
      alert(`Account unlocked. ${user.firstName} can now sign in.`);
      loadUsers();
    } catch { alert('Failed to unlock account'); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Remove this user?')) return;
    try { await usersAPI.delete(id); setUsers(users.filter(u => u.id !== id)); }
    catch { alert('Failed to delete user'); loadUsers(); }
  };

  const openAddForClient = (clientId: number) => {
    setEditingUser(null);
    setFormData({ ...EMPTY_FORM, role: 'Client', clientId });
    setFormSiteIds([]);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM }); setFormSiteIds([]); setEditingUser(null); setShowForm(false);
  };

  const openChangeSiteUser = (user: User) => {
    setChangeSiteUserTarget(user);
    setChangeSiteUserNewSiteId(user.siteId ?? undefined);
    setChangeSiteUserSites([]);
    setShowChangeSiteUserModal(true);
    if (user.clientId) {
      sitesAPI.getByClient(user.clientId)
        .then(r => setChangeSiteUserSites(Array.isArray(r.data) ? r.data : []))
        .catch(() => setChangeSiteUserSites([]));
    }
  };

  const handleChangeSiteUser = async () => {
    if (!changeSiteUserTarget) return;
    setChangeSiteUserSubmitting(true);
    try {
      await usersAPI.changeUserSite(changeSiteUserTarget.id, changeSiteUserNewSiteId ?? null);
      loadUsers();
      setShowChangeSiteUserModal(false);
      setChangeSiteUserTarget(null);
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Failed to change site');
    } finally {
      setChangeSiteUserSubmitting(false);
    }
  };

  if (loading) return <div>Loading users...</div>;

  const clientUsers = users.filter(u => u.clientId != null);
  const staffUsers  = users.filter(u => u.clientId == null);
  const usersForClient = (cid: number) => clientUsers.filter(u => u.clientId === cid);
  // Fleet managers only see their own client
  const visibleClients = isFleetManager
    ? clients.filter(c => c.id === currentUser?.clientId)
    : isOnboarder
      ? clients.filter(c => !c.companyName.trim().toLowerCase().startsWith('yourshield'))
      : clients;

  const UserRow = ({ user }: { user: User }) => (
    <tr>
      <td><strong>{user.username}</strong></td>
      <td>{user.firstName} {user.lastName}</td>
      <td>{user.email}</td>
      <td>
        {user.siteName
          ? <span style={{ fontSize: 12, background: '#ede9fe', color: '#6d28d9', padding: '2px 8px', borderRadius: 9999, fontWeight: 600 }}>{user.siteName}</span>
          : <span style={{ color: '#9ca3af', fontSize: 12 }}>Head Office</span>}
      </td>
      <td><span className={`role-badge role-${roleBadgeClass(user.role)}`}>{formatRole(user.role)}</span></td>
      <td>
        {user.lockedUntil && new Date(user.lockedUntil) > new Date()
          ? <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 9999, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>🔒 Locked</span>
          : <span className={`status-badge status-${user.isActive ? 'active' : 'inactive'}`}>{user.isActive ? 'Active' : 'Inactive'}</span>}
      </td>
      <td style={{ fontSize: 12, color: '#6b7280' }}>{user.lastLoginAt ? formatDateTimeBritish(user.lastLoginAt) : 'Never'}</td>
      <td>
        {user.role === 'SuperAdmin'
          ? <span style={{ color: '#718096', fontSize: '13px', fontStyle: 'italic' }}>Protected</span>
          : isFleetManager || isOnboarder
            ? <div className="action-buttons">
                <button className="btn-icon" onClick={() => handleEdit(user)} title="Edit">✏️</button>
                <button className="btn-icon" onClick={() => openChangeSiteUser(user)} title="Change Site">🏢</button>
                <button className="btn-icon" onClick={() => handleSendInvite(user)} title="Send invite" disabled={sendingInvite === user.id} style={{ opacity: sendingInvite === user.id ? 0.5 : 1 }}>
                  {sendingInvite === user.id ? '⏳' : '✉️'}
                </button>
                {isOnboarder && (
                  <button className="btn-icon" onClick={() => handleResetPassword(user.id)} title="Reset password">🔑</button>
                )}
                {(isOnboarder || user.isActive) && (
                  <button className="btn-icon" onClick={() => handleToggleActive(user.id, user.isActive)} title={user.isActive ? 'Deactivate user' : 'Activate user'}>
                    {user.isActive ? '🔒' : '🔓'}
                  </button>
                )}
              </div>
            : <div className="action-buttons">
                <button className="btn-icon" onClick={() => handleEdit(user)} title="Edit">✏️</button>
                <button className="btn-icon" onClick={() => openChangeSiteUser(user)} title="Change Site">🏢</button>
                {user.lockedUntil && new Date(user.lockedUntil) > new Date()
                  ? <button className="btn-icon" onClick={() => handleUnlock(user)} title="Unlock account" style={{ color: '#dc2626' }}>🔓</button>
                  : <button className="btn-icon" onClick={() => handleToggleActive(user.id, user.isActive)} title={user.isActive ? 'Deactivate' : 'Activate'}>{user.isActive ? '🔒' : '🔓'}</button>}
                <button className="btn-icon" onClick={() => handleResetPassword(user.id)} title="Reset password">🔑</button>
                {isSuperAdmin && (
                  <button className="btn-icon" onClick={() => handleSendInvite(user)} title="Send invite" disabled={sendingInvite === user.id} style={{ opacity: sendingInvite === user.id ? 0.5 : 1 }}>
                    {sendingInvite === user.id ? '⏳' : '✉️'}
                  </button>
                )}
                <button className="btn-icon" onClick={() => handleDelete(user.id)} title="Remove">🗑️</button>
              </div>}
      </td>
    </tr>
  );

  return (
    <div className="user-list">
      <div className="page-header"><h1>User Management</h1></div>

      {/* ── Modal form ── */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Create New User'}</h2>
              <button className="close-btn" onClick={resetForm}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Username *</label>
                  <input
                    type="text"
                    required
                    value={formData.username}
                    onChange={e => setFormData({ ...formData, username: e.target.value.replace(/\s/g, '') })}
                    disabled={!!editingUser}
                    placeholder="e.g. EdytaHaines"
                    autoComplete="off"
                  />
                  {!editingUser && (
                    <small style={{ color: '#718096', fontSize: 12, display: 'block', marginTop: 4 }}>
                      Use a login name — not an email address.
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" required value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })} />
                </div>
              </div>

              {!editingUser && (
                <div className="form-group">
                  <label>Password *</label>
                  <input type="password" required minLength={6} value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })} />
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input type="text" required value={formData.firstName}
                    onChange={e => setFormData({ ...formData, firstName: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input type="text" required value={formData.lastName}
                    onChange={e => setFormData({ ...formData, lastName: e.target.value })} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Role *</label>
                  <select required value={formData.role}
                    onChange={e => setFormData({ ...formData, role: e.target.value, clientId: isFleetManager ? formData.clientId : undefined, siteId: undefined })}>
                    {!isOnboarder && !isFleetManager && isSuperUser && <option value="SuperAdmin">Super Admin</option>}
                    {!isOnboarder && !isFleetManager && isSuperUser && <option value="SuperUser">Super User</option>}
                    {!isOnboarder && !isFleetManager && <option value="Admin">Compliance</option>}
                    {!isOnboarder && !isFleetManager && <option value="FleetManager">Fleet Manager</option>}
                    {!isOnboarder && !isFleetManager && isSuperAdmin && <option value="ClientAdmin">Client Admin</option>}
                    {!isOnboarder && isFleetManagerAdmin && <option value="FleetManager">Fleet Manager</option>}
                    {!isOnboarder && !isFleetManager && <option value="MTA">MTA</option>}
                    {!isOnboarder && !isFleetManager && <option value="Onboarder">Onboarder</option>}
                    {!isOnboarder && !isFleetManager && isSuperUser && <option value="ClaimsManager">Claims Manager</option>}
                    {isOnboarder && <option value="ClientAdmin">Client Admin</option>}
                    <option value="Client">Client</option>
                  </select>
                </div>
                {!isFleetManager && (
                  <div className="form-group">
                    <label>Company / Client{clientRequired ? ' *' : ' (Optional)'}</label>
                    <select required={clientRequired} value={formData.clientId || ''}
                      onChange={e => setFormData({ ...formData, clientId: e.target.value ? parseInt(e.target.value) : undefined, siteId: undefined })}>
                      <option value="">Select a company...</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {/* Site — only for Client role once a client is selected */}
              {formData.role === 'Client' && formData.clientId && (
                <div className="form-group">
                  <label>Primary Site
                    <span style={{ fontWeight: 400, color: '#718096', fontSize: 12, marginLeft: 8 }}>
                      Optional — their site's vehicles appear first in their fleet list
                    </span>
                  </label>
                  <select value={formData.siteId || ''}
                    onChange={e => setFormData({ ...formData, siteId: e.target.value ? parseInt(e.target.value) : undefined })}>
                    <option value="">— No site (Head Office / full fleet view) —</option>
                    {sitesForClient.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  {sitesForClient.length === 0 && (
                    <small style={{ color: '#9ca3af', fontSize: 12 }}>No sites configured for this client yet.</small>
                  )}
                </div>
              )}

              {/* Multi-site access restrictions */}
              {formData.role === 'Client' && formData.clientId && sitesForClient.length > 0 && (
                <div className="form-group">
                  <label>Site Access Restrictions
                    <span style={{ fontWeight: 400, color: '#718096', fontSize: 12, marginLeft: 8 }}>
                      Select sites to restrict this user's vehicle visibility
                    </span>
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                    {sitesForClient.map(s => (
                      <label key={s.id} style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                        border: '1.5px solid', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                        background: formSiteIds.includes(s.id) ? '#ede9fe' : '#f9fafb',
                        borderColor: formSiteIds.includes(s.id) ? '#7c3aed' : '#e2e8f0'
                      }}>
                        <input type="checkbox" checked={formSiteIds.includes(s.id)}
                          onChange={e => setFormSiteIds(e.target.checked
                            ? [...formSiteIds, s.id]
                            : formSiteIds.filter(id => id !== s.id))} />
                        {s.name}
                      </label>
                    ))}
                  </div>
                  <small style={{ color: '#9ca3af', fontSize: 12, marginTop: 4, display: 'block' }}>
                    Leave all unchecked for full fleet access (Head Office view)
                  </small>
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingUser ? 'Update' : 'Create'} User</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Change User Site Modal ── */}
      {showChangeSiteUserModal && changeSiteUserTarget && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change Site</h2>
              <button className="close-btn" onClick={() => setShowChangeSiteUserModal(false)}>✕</button>
            </div>
            <div style={{ padding: '20px 24px 8px' }}>
              <div style={{
                padding: '12px 16px', marginBottom: 20,
                background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8,
                fontSize: 14, color: '#0c4a6e', lineHeight: 1.5,
              }}>
                <span style={{ fontWeight: 700 }}>{changeSiteUserTarget.firstName} {changeSiteUserTarget.lastName}</span>
                {' '}({changeSiteUserTarget.username})
                <div style={{ marginTop: 4, color: '#374151' }}>
                  Currently assigned to: <strong>{changeSiteUserTarget.siteName ?? 'Head Office'}</strong>
                </div>
              </div>
              <div className="form-group">
                <label>Move to Site</label>
                <select
                  value={changeSiteUserNewSiteId ?? ''}
                  onChange={e => setChangeSiteUserNewSiteId(e.target.value ? parseInt(e.target.value) : undefined)}
                >
                  <option value="">— Head Office (no specific site) —</option>
                  {changeSiteUserSites.length === 0
                    ? <option disabled value="__loading__">Loading sites…</option>
                    : changeSiteUserSites.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))
                  }
                </select>
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowChangeSiteUserModal(false)} disabled={changeSiteUserSubmitting}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleChangeSiteUser} disabled={changeSiteUserSubmitting}>
                {changeSiteUserSubmitting ? 'Saving…' : '✓ Update Site'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Client accordion sections ── */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: '#002060', marginBottom: 14 }}>Client Users</h2>
        {visibleClients.length === 0
          ? isFleetManager
            ? <div style={{ padding: '18px 20px', background: '#fff7ed', border: '1.5px solid #fb923c', borderRadius: 10 }}>
                <div style={{ fontWeight: 700, color: '#9a3412', marginBottom: 6 }}>⚠️ Organisation not linked</div>
                <div style={{ fontSize: 14, color: '#7c2d12' }}>
                  Your Fleet Manager account is not assigned to a client company. Ask a Super Admin to open your user account, set the <strong>Company</strong> field to your organisation, then <strong>log out and back in</strong>.
                </div>
              </div>
            : <p className="empty-state">No clients found. Create a client first.</p>
          : visibleClients.map(client => {
              const cUsers = usersForClient(client.id);
              const isOpen = selectedClientId === client.id;
              return (
                <div key={client.id} style={{ marginBottom: 10, border: '1.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                  <button
                    onClick={() => setSelectedClientId(isOpen ? null : client.id)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '14px 20px', background: isOpen ? '#f0f4ff' : '#f9fafb',
                      border: 'none', cursor: 'pointer', textAlign: 'left',
                      borderBottom: isOpen ? '1.5px solid #e2e8f0' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#002060' }}>{client.companyName}</span>
                      <span style={{
                        fontSize: 12, padding: '2px 8px', borderRadius: 9999, fontWeight: 600,
                        background: cUsers.length > 0 ? '#dbeafe' : '#f3f4f6',
                        color: cUsers.length > 0 ? '#1e40af' : '#9ca3af'
                      }}>
                        {cUsers.length} user{cUsers.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <button className="btn btn-primary btn-sm" style={{ fontSize: 12, padding: '4px 12px' }}
                        onClick={e => { e.stopPropagation(); openAddForClient(client.id); }}>
                        <img src={additionIcon} alt="" style={{ width: 12, height: 12, marginRight: 5, verticalAlign: 'middle' }} />
                        Add User
                      </button>
                      <span style={{ color: '#6b7280', fontSize: 14 }}>{isOpen ? '▲' : '▼'}</span>
                    </div>
                  </button>

                  {isOpen && (
                    <div style={{ background: '#fff' }}>
                      {cUsers.length === 0
                        ? <p style={{ padding: '18px 20px', color: '#9ca3af', fontStyle: 'italic', margin: 0 }}>
                            No users yet — click Add User to create the first account for this client.
                          </p>
                        : <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                {['Username','Name','Email','Site','Role','Status','Last Login','Actions'].map(h => (
                                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 700 }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>{cUsers.map(u => <UserRow key={u.id} user={u} />)}</tbody>
                          </table>}
                    </div>
                  )}
                </div>
              );
            })}
      </div>

      {/* ── Staff section ── */}
      {!isFleetManager && !isOnboarder && staffUsers.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#002060', margin: 0 }}>Staff &amp; Admin Users</h2>
            <button className="btn btn-primary btn-sm" style={{ fontSize: 12 }}
              onClick={() => { setEditingUser(null); setFormData({ ...EMPTY_FORM, role: 'Admin' }); setShowForm(true); }}>
              <img src={additionIcon} alt="" style={{ width: 12, height: 12, marginRight: 5, verticalAlign: 'middle' }} />
              Add Staff User
            </button>
          </div>
          <div className="card">
            <table>
              <thead>
                <tr>{['Username','Name','Email','Site','Role','Status','Last Login','Actions'].map(h => <th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>{staffUsers.map(u => <UserRow key={u.id} user={u} />)}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
